import { z } from 'zod';
import { canonicalSwapTradeSchema } from './canonicalSwapEvents.js';

const address = z.string().regex(/^0x[a-f0-9]{40}$/);
const hash = z.string().regex(/^0x[a-f0-9]{64}$/);
const uint = z.string().max(78).regex(/^(0|[1-9][0-9]*)$/);
const validAmount = (value: string) => value.length <= 39 && /^(0|[1-9][0-9]*)$/.test(value) && BigInt(value) < 2n ** 128n;
export const curveTradeObservationSchema = z.strictObject({ schemaVersion: z.literal(1), sourceKind: z.literal('curve'), launchId: address,
  baseAsset: address, quoteAsset: address, blockNumber: uint, blockHash: hash, transactionHash: hash, logIndex: uint,
  timestamp: z.iso.datetime(), observedAt: z.iso.datetime(), side: z.enum(['buy', 'sell']), baseAmount: uint,
  grossQuote: uint, feeQuote: uint, marketQuoteAmount: uint, executionQuote: uint, refundQuote: uint,
  feeBps: z.number().int().min(0).max(9999), payer: address, recipient: address, beneficialOwner: z.null(),
  attribution: z.literal('unknown'), amountScope: z.literal('curve-execution-not-vault-cashflow'),
}).superRefine((t, ctx) => {
  const amounts = [t.baseAmount, t.grossQuote, t.feeQuote, t.marketQuoteAmount, t.executionQuote, t.refundQuote];
  if (!amounts.every(validAmount)) { ctx.addIssue({ code: 'custom', message: 'Invalid uint128 curve amount' }); return; }
  const gross = BigInt(t.grossQuote), fee = BigInt(t.feeQuote), net = gross - fee;
  if (BigInt(t.baseAmount) <= 0n || net <= 0n || fee !== gross * BigInt(t.feeBps) / 10000n
    || BigInt(t.marketQuoteAmount) !== (t.side === 'buy' ? net : gross)
    || BigInt(t.executionQuote) !== (t.side === 'buy' ? gross : net)
    || (t.side === 'sell' && t.refundQuote !== '0') || t.baseAsset === t.quoteAsset)
    ctx.addIssue({ code: 'custom', message: 'Curve trade amount mismatch' });
});
export const ownLaunchTradeSchema = z.union([
  curveTradeObservationSchema,
  canonicalSwapTradeSchema.safeExtend({ sourceKind: z.literal('graduated-pool'), marketQuoteAmount: uint,
    feeQuote: uint, feeBps: z.number().int().min(0).max(9999) }).superRefine((t, ctx) => {
    if (t.marketQuoteAmount !== t.poolQuoteAmount || t.feeQuote !== t.hookFeeQuote || t.feeBps !== t.hookFeeBps)
      ctx.addIssue({ code: 'custom', message: 'Canonical observation alias mismatch' });
  }),
]);
export type OwnLaunchTrade = z.infer<typeof ownLaunchTradeSchema>;
